import { setXlsxContext } from '@features/ui/uiSlice';
import React, { useState, useEffect, useRef, useCallback } from "react";
import { CHAT_PANEL_TRANSITION_MS } from "@constants/chat";
import { useConfirm } from "@hooks/useConfirm";
import { useDeferredUnmount } from "@hooks/useDeferredUnmount";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectIsAuthenticated, selectUser } from "../../../features/auth/authSlice";
import { CUSTOMER_DEMO_TENANT_ID } from "@constants/tenantProfiles";
import { chatService, streamFailureKey } from "../../../services/chatService";
import { fileService, FileInfo } from "../../../services/fileService";
import { ChatThread, ChatMessage, SlashCommand, SLASH_COMMANDS } from "../../../types/chat";
import { ExtendedChatMessage } from "../NotionStyleMessageList";
import type { SearchResult } from "../SearchResultsCard";
import { useAppDispatch, useAppSelector } from "@store/index";
import { visibleSlashCommands } from "../chatIntent";
import {
  selectCurrentMode,
  selectCurrentModeData,
  setCurrentMode,
  setShowModeSelector,
  switchMode,
  selectActiveModes,
} from "@features/chat/chatModeSlice";
import { useTranslation } from "react-i18next";
import { sortThreadsForHistory } from "../chatHistory";
import { mapApiResultsToCards } from "../mapSearchResults";
import { isFileSummaryRequest, nextFileSuggestionState } from "../chatIntent";
import { AttachedFile, SourceType } from "./types";
import { buildDocumentSourceUrl, isDeepAnalysisRequest } from "./helpers";
import { searchResultTitle } from '../searchResultTitle';

export const useChatPanelController = (open: boolean) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const confirm = useConfirm();
  const { t, i18n } = useTranslation();
  const xlsxContext = useAppSelector((state) => state.ui.xlsxContext);
  const clearXlsxContext = () => dispatch(setXlsxContext(null));
  const [inputValue, setInputValue] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [sourceType, setSourceType] = useState<SourceType>("all");
  const [showFileSuggestions, setShowFileSuggestions] = useState(false);
  const [selectedChat, setSelectedChat] = useState(() => t("chat.newChat"));
  const [isComposing, setIsComposing] = useState(false);
  const [showSlashSuggestions, setShowSlashSuggestions] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Ref to track attached files synchronously (fixes timing issue #122)
  const attachedFilesRef = useRef<AttachedFile[]>([]);
  // Ref to track latest selected thread ID for race condition prevention
  const latestThreadIdRef = useRef<string | null>(null);
  // Every async conversation operation owns a version. Older history reads and
  // streams must not change the conversation selected in this browser tab.
  const conversationVersionRef = useRef(0);
  const busyRef = useRef(false);
  const initializedConversationRef = useRef(false);
  // Creation owns the conversation until its POST settles. New-chat and history
  // selection must check this before advancing the version; sends check busyRef.
  // Only an identity change may supersede creation, and that effect resets these
  // guards. An old request must not clear a newer identity's creation guard.
  const creatingThreadRef = useRef(false);
  // Prevent duplicate confirmations/requests before React renders their state.
  // Each operation releases its own pending guard; identity changes reset both.
  const newChatPendingRef = useRef(false);
  const pendingThreadSelectionRef = useRef<string | null>(null);

  // Real API state
  const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentThread, setCurrentThread] = useState<ChatThread | null>(null);
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [isThreadsLoading, setIsThreadsLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pinningThreadId, setPinningThreadId] = useState<string | null>(null);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);
  const userId = user?.id;
  const tenantId = user?.tenantId;
  // Mounted through the exit so the slide-out has something to play on.
  const isMounted = useDeferredUnmount(open, CHAT_PANEL_TRANSITION_MS);

  // Mode system selectors (Issue #302)
  const activeModes = useAppSelector(selectActiveModes);
  const currentMode = useAppSelector(selectCurrentMode);
  const currentModeData = useAppSelector(selectCurrentModeData);

  // Kept in a ref for synchronous access in async handlers (issue #122).
  useEffect(() => {
    attachedFilesRef.current = attachedFiles;
  }, [attachedFiles]);

  // Convert ChatMessage[] to ExtendedChatMessage[]
  const convertToExtendedMessages = useCallback(
    (threadMessages: ChatMessage[]): ExtendedChatMessage[] =>
      threadMessages.map((msg) => ({
        ...msg,
        searchResults: msg.searchResults
          ? {
              query: msg.searchResults.query,
              results: msg.searchResults.results.map((r) => ({
                ...r,
                id: r.id,
                title: r.title,
                path: r.fileName ? [r.fileName] : [],
                type: "document" as const,
                snippet: r.snippet,
                relevance: r.relevanceScore,
                relevanceLabel: r.relevanceLabel,
                rawRelevance: r.rawRelevanceScore,
                relevanceSource: r.relevanceSource,
              })),
            }
          : undefined,
      })),
    [],
  );

  useEffect(() => {
    conversationVersionRef.current += 1;
    latestThreadIdRef.current = null;
    busyRef.current = false;
    creatingThreadRef.current = false;
    newChatPendingRef.current = false;
    pendingThreadSelectionRef.current = null;
    dispatch(setXlsxContext(null));
    initializedConversationRef.current = false;
    setCurrentThread(null);
    setMessages([]);
    setThreads([]);
    setInputValue("");
    setAttachedFiles([]);
    attachedFilesRef.current = [];
    setIsLoading(false);
    setSelectedChat(t("chat.newChat"));
    return () => { conversationVersionRef.current += 1; };
    // A locale change must not discard the current conversation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, tenantId, isAuthenticated]);

  // Refresh shared history without replacing this tab's current conversation.
  useEffect(() => {
    let cancelled = false;
    const version = conversationVersionRef.current;
    const loadThreads = async () => {
      if (open && isAuthenticated) {
        try {
          const fetchedThreads = await chatService.getThreads();
          if (cancelled || version !== conversationVersionRef.current) return;
          const sortedThreads = sortThreadsForHistory(fetchedThreads);
          setThreads(sortedThreads);
          if (initializedConversationRef.current) return;
          // Demo accounts are shared by several participants. Choosing the
          // account's newest thread would adopt someone else's conversation.
          if (tenantId !== CUSTOMER_DEMO_TENANT_ID && sortedThreads.length > 0) {
            latestThreadIdRef.current = sortedThreads[0].id;
            setCurrentThread(sortedThreads[0]);
            setSelectedChat(sortedThreads[0].title || t("chat.chatFallback"));
            setAttachedFiles([]);
            attachedFilesRef.current = [];
            // Load messages for the first thread
            const threadMessages = await chatService.getMessages(sortedThreads[0].id);
            if (cancelled || version !== conversationVersionRef.current) return;
            setMessages(convertToExtendedMessages(threadMessages));
          }
          initializedConversationRef.current = true;
          // Don't auto-create a thread - let user click "New Chat" button
        } catch (error) {
          console.error("Failed to load threads:", error);
        }
      }
    };

    loadThreads();
    return () => { cancelled = true; };
    // Load threads on open/auth only. t() just supplies a fallback chat title;
    // making it a dependency would reload threads and reset the user's selected
    // thread whenever the language changes (PR #790 review).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated, userId, tenantId, convertToExtendedMessages]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Check for slash command (Issue #302)
    if (newValue.startsWith("/")) {
      setShowSlashSuggestions(true);
      setShowFileSuggestions(false);
    } else {
      setShowSlashSuggestions(false);
    }

    // Check for @ mention - open file search panel
    const nextFileSuggestions = nextFileSuggestionState(newValue, showFileSuggestions);
    setShowFileSuggestions(nextFileSuggestions);
    if (nextFileSuggestions && !showFileSuggestions) {
      setShowSlashSuggestions(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !isComposing) {
      event.preventDefault();
      // Don't send if file suggestions are open
      if (showFileSuggestions) {
        return;
      }
      // For slash commands, only block if there are actually matching commands
      // to prevent blocking users from sending messages like "/xyz"
      if (showSlashSuggestions && inputValue.startsWith("/")) {
        // The same list the popup renders, so Enter is never swallowed by an
        // empty popup.
        const hasMatchingCommands =
          visibleSlashCommands(SLASH_COMMANDS, inputValue, activeModes).length > 0;
        if (hasMatchingCommands) {
          return; // Block Enter when there are matching suggestions
        }
      }
      handleSendMessage();
    }
    if (event.key === "Escape") {
      if (showFileSuggestions) {
        setShowFileSuggestions(false);
      }
      if (showSlashSuggestions) {
        setShowSlashSuggestions(false);
      }
    }
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsComposing(false);
  };

  // Slash command selection handler (Issue #302)
  const handleSlashCommandSelect = useCallback(
    (command: SlashCommand) => {
      // Special handling for DRBFM - navigate to dedicated page
      if (command.modeId === "drbfm") {
        setInputValue("");
        setShowSlashSuggestions(false);
        navigate("/drbfm");
        return;
      }

      // Switch mode - use async switchMode for persistence if thread exists
      if (currentThread?.id) {
        dispatch(
          switchMode({
            threadId: currentThread.id,
            request: { mode_id: command.modeId },
          }),
        );
      } else {
        dispatch(setCurrentMode(command.modeId));
      }
      // Clear input and close suggestions
      setInputValue("");
      setShowSlashSuggestions(false);
      // Add system message about mode switch
      const systemMsg: ExtendedChatMessage = {
        id: `system-mode-${Date.now()}`,
        role: "system" as const,
        // Not keyed, deliberately: the values are themselves per-mode translations
        // with their own fallbacks, so one key cannot carry them and a keyed version
        // relocalizes the sentence while freezing the mode name — worse than neither.
        content: t("chat.modeSwitched", {
          mode: t(`chat.modes.${command.modeId}.name`, command.label),
          description: t(`chat.modes.${command.modeId}.description`, command.description),
        }),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, systemMsg]);
      // Focus back to input
      inputRef.current?.focus();
    },
    [dispatch, currentThread?.id, navigate, t],
  );

  // Close slash suggestions handler
  const handleCloseSlashSuggestions = useCallback(() => {
    setShowSlashSuggestions(false);
  }, []);

  const handleFileSummarization = async (
    userMessage: string,
    assistantId: string,
    isCurrent: () => boolean,
  ) => {
    try {
      // Use ref for immediate access to latest attached files (fixes timing issue #122)
      const currentAttachedFiles = attachedFilesRef.current;
      if (currentAttachedFiles.length === 0) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantId
              ? {
                  ...msg,
                  content: "",
                  contentKey: { messageKey: "chat.noFilesSelectedForSummary" },
                }
              : msg,
          ),
        );
        return;
      }

      const fileIds = currentAttachedFiles.map((f) => f.id);
      const useDeepAnalysis = isDeepAnalysisRequest(userMessage);

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? {
                ...msg,
                content: "",
                contentKey: {
                  messageKey: useDeepAnalysis
                    ? "chat.deepAnalysisInProgress"
                    : "chat.fileAnalysisInProgress",
                },
              }
            : msg,
        ),
      );

      // Get file summaries with deep analysis if requested
      const summaryResponse = await fileService.getMultipleFileSummaries(
        fileIds,
        t,
        true,
        true,
        useDeepAnalysis,
      );
      if (!isCurrent()) return;

      // Build the response message
      let responseContent = "";

      if (fileIds.length === 1) {
        // Single file summary
        const fileId = fileIds[0];
        const file = attachedFiles.find((f) => f.id === fileId);
        const summary =
          summaryResponse.individualSummaries?.[fileId] ||
          t("chat.summaryUnavailable");
        const summaryType = useDeepAnalysis
          ? t("chat.deepTechnicalAnalysis")
          : t("chat.summary");

        responseContent = `${t("chat.singleFileSummaryHeading", {
          title: file?.title || file?.name,
          type: summaryType,
        })}\n\n${summary}`;
      } else {
        // Multiple file summaries
        const summaryType = useDeepAnalysis
          ? t("chat.deepTechnicalAnalysis")
          : t("chat.summary");
        responseContent = `${t("chat.multiFileSummaryHeading", {
          count: fileIds.length,
          type: summaryType,
        })}\n\n`;

        // Individual summaries
        if (summaryResponse.individualSummaries) {
          responseContent += `${t(useDeepAnalysis
            ? "chat.individualFileAnalysesHeading"
            : "chat.individualFileSummariesHeading")}\n\n`;
          attachedFiles.forEach((file, index) => {
            const summary =
              summaryResponse.individualSummaries?.[file.id] ||
              t("chat.summaryUnavailable");
            responseContent += `**${index + 1}. ${file.title || file.name}**\n${summary}\n\n`;
          });
        }

        // Combined summary - different header based on analysis type
        if (summaryResponse.combinedSummary) {
          responseContent += useDeepAnalysis
            ? `${t("chat.combinedTechnicalAnalysisHeading")}\n\n`
            : `${t("chat.combinedTechnicalInsightsHeading")}\n\n`;
          responseContent += summaryResponse.combinedSummary;
        }
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId ? { ...msg, content: responseContent } : msg,
        ),
      );
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Failed to get file summaries:", error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? {
                ...msg,
                content: "",
                contentKey: { messageKey: "chat.summaryError" },
              }
            : msg,
        ),
      );
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || busyRef.current) return;
    busyRef.current = true;
    setIsLoading(true);
    initializedConversationRef.current = true;
    const version = ++conversationVersionRef.current;
    const isCurrent = () => version === conversationVersionRef.current;

    const userMessage = inputValue.trim();
    const selection = xlsxContext;

    // Auto-create a thread if none exists
    let activeThread = currentThread;
    if (!activeThread) {
      creatingThreadRef.current = true;
      try {
        activeThread = await chatService.createThread(t("chat.newChat"));
        if (!isCurrent()) return;
        latestThreadIdRef.current = activeThread.id;
        setCurrentThread(activeThread);
        setSelectedChat(activeThread.title || t("chat.newChat"));
        setThreads((prev) => sortThreadsForHistory([activeThread!, ...prev]));
      } catch (error) {
        console.error("Failed to create thread:", error);
        if (!isCurrent()) return;
        busyRef.current = false;
        setIsLoading(false);
        return;
      } finally {
        if (isCurrent()) creatingThreadRef.current = false;
      }
    }
    setInputValue("");
    setIsLoading(true);

    // Add context indicator if files are selected (only if not already shown for same files)
    const currentFiles = attachedFilesRef.current;
    if (!selection && currentFiles.length > 0 && !isFileSummaryRequest(userMessage)) {
      const fileNames = currentFiles.map((f) => f.title || f.name).join(", ");
      const contextKey = {
        messageKey: "chat.askingAboutFiles",
        values: { count: currentFiles.length, fileNames },
      };
      setMessages((prev) => {
        // Dedupe on the KEY: comparing rendered text missed after a language switch,
        // and the "📎" prefix it tested is copy, not structure.
        const lastContextMsg = [...prev].reverse()
          .find((m) => m.role === "system" && m.contentKey?.messageKey === contextKey.messageKey);
        if (lastContextMsg
          && JSON.stringify(lastContextMsg.contentKey?.values) === JSON.stringify(contextKey.values)) {
          return prev;
        }
        const contextMsg: ExtendedChatMessage = {
          id: `context-${Date.now()}`,
          role: "system" as const,
          content: "",
          contentKey: contextKey,
          timestamp: new Date(),
        };
        return [...prev, contextMsg];
      });
    }

    // Add user message immediately
    const userMsg: ExtendedChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: userMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);

    // Initialize assistant message for streaming
    const assistantId = Date.now().toString() + "-assistant";
    const assistantMsg: ExtendedChatMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, assistantMsg]);

    try {
      // Check if this is a file summary request
      if (!selection && isFileSummaryRequest(userMessage)) {
        await handleFileSummarization(userMessage, assistantId, isCurrent);
        // Keep attached files for potential follow-up questions
        return;
      }

      let fullMessage = "";

      // Build selectedFiles from attachedFiles for deep-dive context
      const selectedFileIds = attachedFilesRef.current.map((f) => f.id);

      // Stream the message with mode context (Issue #302) and selected files
      await chatService.streamMessage(
        activeThread.id,
        userMessage,
        (content) => {
          if (!isCurrent()) return;
          fullMessage = content;
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId ? { ...msg, content: fullMessage } : msg,
            ),
          );
        },
        (results) => {
          if (!isCurrent()) return;
          const mappedResults = mapApiResultsToCards(results);

          // Update message with search results
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId
                ? {
                    ...msg,
                    searchResults:
                      results.length > 0
                        ? {
                            query: userMessage,
                            results: mappedResults,
                          }
                        : undefined,
                  }
                : msg,
            ),
          );
        },
        (modeInfo) => {
          if (!isCurrent()) return;
          // On complete - handle mode info from response (Issue #302)
          if (modeInfo?.modeSwitched && modeInfo.newMode) {
            dispatch(setCurrentMode(modeInfo.newMode));
          }
          // Store grounding status + verification/web-search summaries on the assistant message
          if (
            modeInfo?.groundingStatus ||
            modeInfo?.completeness ||
            modeInfo?.verificationSummary ||
            modeInfo?.webSearchStatus
          ) {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantId
                  ? {
                      ...msg,
                      ...(modeInfo.groundingStatus && { groundingStatus: modeInfo.groundingStatus }),
                      ...(modeInfo.completeness && { completeness: modeInfo.completeness }),
                      ...(modeInfo.verificationSummary && { verificationSummary: modeInfo.verificationSummary }),
                      ...(modeInfo.webSearchStatus && { webSearchStatus: modeInfo.webSearchStatus }),
                    }
                  : msg,
              ),
            );
          }
        },
        {
          xlsxAnchor: selection || undefined,
          preferredLanguage: i18n.language?.startsWith('en') ? 'en' : 'ja',
          mode: currentMode,
          modeData: currentModeData,
          includeWebSearch: sourceType === 'web',
          selectedFiles:
            selectedFileIds.length > 0 ? selectedFileIds : undefined,
          onLiteralValues: (literalValues: string[]) => {
            if (!isCurrent()) return;
            setMessages((prev) =>
              prev.map((msg) => (msg.id === assistantId ? { ...msg, literalValues } : msg)),
            );
          },
          onTitleUpdate: (title: string) => {
            if (!isCurrent()) return;
            setSelectedChat((prevChat) => {
              if (latestThreadIdRef.current === activeThread.id) {
                return title;
              }
              return prevChat;
            });
            setCurrentThread((prev) => {
              if (prev && prev.id === activeThread.id) {
                return { ...prev, title };
              }
              return prev;
            });
            setThreads((prev) =>
              sortThreadsForHistory(prev.map((t) =>
                t.id === activeThread.id ? { ...t, title } : t,
              )),
            );
          },
        },
      );

      // Attached files persist until user explicitly removes them
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Failed to send message:", error);
      // Say which failure happened in the bubble the user is watching. Deleting
      // it left an answered-looking thread with the answer silently missing (#1086).
      // The write that persists the answer runs *after* it streams, so a failure
      // can arrive with text already on screen: keep that text and append the
      // notice, rather than discarding an answer the user has already read.
      const noticeKey = error instanceof Error && error.message.startsWith("Excel:")
        ? "documents.cells.selectionFailed"
        : streamFailureKey(error) ?? "chat.streamError";
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? { ...msg, noticeKey }
            : msg,
        ),
      );
    } finally {
      // A stale stream must not unlock a newer send. The active operation
      // releases the composer on success, server error, and connection loss.
      if (isCurrent()) {
        busyRef.current = false;
        setIsLoading(false);
      }
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleAddContext = () => {
    setShowFileSuggestions(true);
  };


  const createNewThread = async () => {
    if (creatingThreadRef.current) return;
    creatingThreadRef.current = true;
    initializedConversationRef.current = true;
    const version = ++conversationVersionRef.current;
    busyRef.current = true;
    latestThreadIdRef.current = null;
    try {
      setIsLoading(true);
      const thread = await chatService.createThread(t("chat.newChat"));
      if (version !== conversationVersionRef.current) return;
      latestThreadIdRef.current = thread.id;
      setCurrentThread(thread);
      setMessages([]);
      dispatch(setXlsxContext(null));
      setInputValue("");
      setAttachedFiles([]);
      attachedFilesRef.current = [];
      setSelectedChat(thread.title || t("chat.newChat"));
      setThreads((prev) => sortThreadsForHistory([thread, ...prev]));
    } catch (error) {
      console.error("Failed to create thread:", error);
    } finally {
      if (version === conversationVersionRef.current) {
        creatingThreadRef.current = false;
        busyRef.current = false;
        setIsLoading(false);
      }
    }
  };

  /**
   * Starting over and switching threads both drop the attached files and
   * abandon any reply still streaming, so the body names whichever of those is
   * actually at risk rather than always claiming it is the attachments.
   */
  const confirmDropContext = async (action: "newChat" | "switchThread") => {
    if (attachedFiles.length === 0 && !xlsxContext && !isLoading) return true;
    const reason = attachedFiles.length > 0 || xlsxContext ? "attachments" : "streaming";
    return confirm({
      title: t(`chat.${action}Confirm.title`),
      description: t(`chat.${action}Confirm.${reason}`),
      confirmLabel: t(`chat.${action}Confirm.confirm`),
    });
  };

  const handleNewChat = async () => {
    if (creatingThreadRef.current || newChatPendingRef.current) return;
    const version = conversationVersionRef.current;
    newChatPendingRef.current = true;
    try {
      if (!(await confirmDropContext("newChat"))) return;
      if (version !== conversationVersionRef.current) return;
      await createNewThread();
    } finally {
      newChatPendingRef.current = false;
    }
  };

  const handleHistoryOpenChange = async (open: boolean) => {
    setHistoryOpen(open);
    if (!open) return;

    setIsThreadsLoading(true);
    try {
      const latestThreads = await chatService.getThreads();
      setThreads(sortThreadsForHistory(latestThreads));
    } catch (error) {
      console.error("Failed to refresh threads:", error);
    } finally {
      setIsThreadsLoading(false);
    }
  };

  const handleHistoryMenuClose = () => {
    setHistoryOpen(false);
  };

  const handleThreadSelect = async (thread: ChatThread) => {
    if (creatingThreadRef.current || pendingThreadSelectionRef.current === thread.id) return;
    if (thread.id === currentThread?.id) {
      handleHistoryMenuClose();
      return;
    }
    pendingThreadSelectionRef.current = thread.id;
    const confirmationVersion = conversationVersionRef.current;
    const confirmed = await confirmDropContext("switchThread");
    if (pendingThreadSelectionRef.current === thread.id) pendingThreadSelectionRef.current = null;
    if (!confirmed || confirmationVersion !== conversationVersionRef.current
      || creatingThreadRef.current || latestThreadIdRef.current === thread.id) return;
    handleHistoryMenuClose();

    const selectedThreadId = thread.id;
    const version = ++conversationVersionRef.current;
    initializedConversationRef.current = true;
    busyRef.current = true;
    latestThreadIdRef.current = selectedThreadId;

    setCurrentThread(thread);
    setSelectedChat(thread.title || t("chat.newChat"));
    dispatch(setXlsxContext(null));
    setAttachedFiles([]);
    attachedFilesRef.current = [];
    setMessages([]);
    setIsLoading(true);

    try {
      const threadMessages = await chatService.getMessages(selectedThreadId);
      if (version === conversationVersionRef.current) {
        setMessages(convertToExtendedMessages(threadMessages));
      }
    } catch (error) {
      console.error("Failed to load thread messages:", error);
    } finally {
      if (version === conversationVersionRef.current) {
        busyRef.current = false;
        setIsLoading(false);
      }
    }
  };

  const handleThreadPinToggle = async (
    event: React.MouseEvent<HTMLButtonElement>,
    thread: ChatThread,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const nextPinned = !thread.isPinned;
    const optimisticThread: ChatThread = {
      ...thread,
      isPinned: nextPinned,
      pinnedAt: nextPinned ? new Date() : null,
    };

    setPinningThreadId(thread.id);
    setThreads((prev) =>
      sortThreadsForHistory(prev.map((item) =>
        item.id === thread.id ? optimisticThread : item,
      )),
    );
    setCurrentThread((prev) =>
      prev?.id === thread.id ? { ...prev, ...optimisticThread } : prev,
    );

    try {
      const updatedThread = await chatService.updateThreadPin(thread.id, nextPinned);
      setThreads((prev) =>
        sortThreadsForHistory(prev.map((item) =>
          item.id === thread.id ? { ...item, ...updatedThread } : item,
        )),
      );
      setCurrentThread((prev) =>
        prev?.id === thread.id ? { ...prev, ...updatedThread } : prev,
      );
    } catch (error) {
      console.error("Failed to update thread pin:", error);
      setThreads((prev) =>
        sortThreadsForHistory(prev.map((item) =>
          item.id === thread.id ? thread : item,
        )),
      );
      setCurrentThread((prev) =>
        prev?.id === thread.id ? { ...prev, ...thread } : prev,
      );
    } finally {
      setPinningThreadId(null);
    }
  };

  const handleSearchResultClick = (result: SearchResult, query: string) => {
    if (result.type === 'web' && result.sourceUrl) {
      window.open(result.sourceUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(buildDocumentSourceUrl(result, query));
  };

  const handleAddResultsToContext = async (results: SearchResult[]) => {
    // Single file processing for immediate feedback
    for (const result of results) {
      try {
        // Check if already attached
        if (attachedFiles.some((f) => f.id === result.id)) {
          continue;
        }

        // Fetch the file data from the list to get complete FileInfo
        const allFiles = await fileService.getFilesForSelection();
        const fileInfo = allFiles.find((f) => f.id === result.id);

        let attachedFile: AttachedFile;
        if (fileInfo) {
          // Convert FileInfo to AttachedFile
          attachedFile = {
            id: fileInfo.id,
            name: fileInfo.name,
            path: fileInfo.path,
            type: fileInfo.type as "file" | "document" | "code",
            title: fileInfo.title,
            description: fileInfo.description,
          };
        } else {
          // Fallback: create AttachedFile from search result
          const label = searchResultTitle(t, result);
          attachedFile = {
            id: result.id,
            name: label,
            path: result.path.join("/"),
            type: result.type as "file" | "document" | "code",
            title: label,
            description: result.snippet,
          };
        }

        // Add single file to attached files immediately
        // Update ref synchronously for immediate availability (fixes timing issue #122)
        attachedFilesRef.current = [
          ...attachedFilesRef.current,
          attachedFile,
        ];
        setAttachedFiles((prev) => [...prev, attachedFile]);

        // Show confirmation message for single file
        setMessages((prev) => [
          ...prev,
          {
            id: `system-${Date.now()}-${result.id}`,
            role: "system" as const,
            content: "",
            contentKey: {
              messageKey: "chat.fileAddedToContext",
              values: { name: attachedFile.title || attachedFile.name },
            },
            timestamp: new Date(),
          } as ExtendedChatMessage,
        ]);
      } catch (error) {
        console.error("Error adding file to context:", error);
      }
    }
  };

  const handleRetryWithMode = async (message: string) => {
    if (
      inputValue.trim().length > 0 &&
      !(await confirm({
        title: t("chat.retryWithModeConfirm.title"),
        description: t("chat.retryWithModeConfirm.description"),
        confirmLabel: t("chat.retryWithModeConfirm.confirm"),
        destructive: false,
      }))
    ) {
      return;
    }
    setInputValue(message);
    dispatch(setShowModeSelector(true));
  };

  const handlePanelSelectFiles = (
    selectedFiles: FileInfo[],
    allPanelFileIds: string[],
  ) => {
    // IDs of all files the panel knows about (selected or not)
    const panelManagedIds = new Set(allPanelFileIds);

    // Convert panel selections to AttachedFile format
    const panelAttachedFiles: AttachedFile[] = selectedFiles.map(
      (file) => ({
        id: file.id,
        name: file.name,
        path: file.path,
        type: file.type as "file" | "document" | "code",
        title: file.title,
        description: file.description,
      }),
    );

    // Keep files from other sources (e.g., SearchResultsCard) that the panel doesn't manage
    const externalFiles = attachedFiles.filter(
      (af) => !panelManagedIds.has(af.id),
    );

    // Replace placeholder files with real metadata from externalFiles if available
    const externalFilesMap = new Map(
      externalFiles.map((f) => [f.id, f]),
    );
    const panelFilesWithRealMetadata = panelAttachedFiles.map(
      (f) => {
        // If this file has placeholder data ('Unknown') and we have real metadata, use the real data
        if (f.name === "Unknown" && externalFilesMap.has(f.id)) {
          return externalFilesMap.get(f.id)!;
        }
        return f;
      },
    );

    // Filter out files from externalFiles that were merged into panelFilesWithRealMetadata
    const panelFileIds = new Set(
      panelFilesWithRealMetadata.map((f) => f.id),
    );
    const remainingExternalFiles = externalFiles.filter(
      (f) => !panelFileIds.has(f.id),
    );

    // Replace the entire state (idempotent)
    const merged = [
      ...remainingExternalFiles,
      ...panelFilesWithRealMetadata,
    ];
    attachedFilesRef.current = merged;
    setAttachedFiles(merged);
  };

  const dateLocale = i18n.language?.startsWith("ja") ? "ja-JP" : "en-US";

  return {
    xlsxContext,
    clearXlsxContext,
    inputValue,
    attachedFiles,
    sourceType,
    setSourceType,
    showFileSuggestions,
    setShowFileSuggestions,
    selectedChat,
    showSlashSuggestions,
    inputRef,
    messages,
    isLoading,
    currentThread,
    threads,
    isThreadsLoading,
    historyOpen,
    pinningThreadId,
    isMounted,
    dateLocale,
    handleInputChange,
    handleKeyPress,
    handleCompositionStart,
    handleCompositionEnd,
    handleSlashCommandSelect,
    handleCloseSlashSuggestions,
    handleSendMessage,
    handleRemoveFile,
    handleAddContext,
    handleNewChat,
    handleHistoryOpenChange,
    handleThreadSelect,
    handleThreadPinToggle,
    handleSearchResultClick,
    handleAddResultsToContext,
    handleRetryWithMode,
    handlePanelSelectFiles,
  };
};
